import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { groupsAPI } from '../api';

function GroupList() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [notice, setNotice] = useState('');

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.notice) {
      setNotice(location.state.notice);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  useEffect(() => {
    loadGroups();
  }, []);

  const loadGroups = async () => {
    try {
      const response = await groupsAPI.getUserGroups();
      setGroups(response.data.groups);
    } catch (err) {
      setError('Failed to load groups');
    } finally {
      setLoading(false);
    }
  };

  const handleGroupClick = (groupId) => {
    navigate(`/groups/${groupId}`);
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card">
        <div className="group-list-header">
          <h2>My Secret Santa Groups</h2>
          <button
            className="btn btn-primary"
            onClick={() => setShowCreateForm(!showCreateForm)}
          >
            {showCreateForm ? 'Cancel' : 'Create New Group'}
          </button>
        </div>

        {notice && (
          <div className="info-message" style={{ marginBottom: '1rem' }}>
            {notice}
          </div>
        )}

        {error && <div className="error-message">{error}</div>}

        {showCreateForm && <CreateGroupForm onSuccess={() => {
          setShowCreateForm(false);
          loadGroups();
        }} />}

        {groups.length === 0 && !showCreateForm ? (
          <div className="info-message" style={{ marginTop: '2rem' }}>
            You haven't joined any Secret Santa groups yet. Create one to get started!
          </div>
        ) : (
          <div className="groups-grid">
            {groups.map((group) => (
              <div
                key={group.id}
                className="group-card"
                onClick={() => handleGroupClick(group.id)}
              >
                <h3>{group.name}</h3>
                {group.description && <p>{group.description}</p>}
                <p>
                  <strong>Organizer:</strong> {group.organizer_name}
                </p>
                <p>
                  <strong>Members:</strong> {group.member_count}
                </p>
                <span className={`group-badge ${group.is_assigned ? 'badge-assigned' : 'badge-pending'}`}>
                  {group.is_assigned ? 'Assigned' : 'Pending'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CreateGroupForm({ onSuccess }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    organizer_opts_out: false,
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await groupsAPI.createGroup(
        formData.name,
        formData.description,
        formData.organizer_opts_out
      );
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create group');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ marginTop: '2rem', padding: '1.5rem', background: '#f8f9fa', borderRadius: '10px' }}>
      <h3>Create New Group</h3>
      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="name">Group Name *</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            placeholder="e.g., Office Secret Santa 2024"
          />
        </div>

        <div className="form-group">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Optional: Add details about gift budget, exchange date, etc."
          />
        </div>

        <div className="form-group checkbox-group">
          <input
            type="checkbox"
            id="organizer_opts_out"
            name="organizer_opts_out"
            checked={formData.organizer_opts_out}
            onChange={handleChange}
          />
          <label htmlFor="organizer_opts_out">
            I don't want to participate (organizer only)
          </label>
        </div>

        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Creating...' : 'Create Group'}
        </button>
      </form>
    </div>
  );
}

export default GroupList;
